import { useInfiniteQuery, useQuery } from '@tanstack/vue-query';
import { api } from '/app/plugins/services';
import { useAuth } from '/app/composables/auth';
import { computed, toValue } from 'vue';
import { useRouter } from 'vue-router';
import { hasTemplate, renderHighlight, renderNeuron } from '/app/components/game-dash/notification/template-context';

// Entity fields reach the client in two casings — the legacy PascalCase
// serialization (`FirstName`, `ResourceType`, `Link`) and the current lowercase
// one (`firstname`, `resourcetype`, `link`) — so every read goes through a
// lowercased view of the object.
const lowerKeys = (value) => (value && typeof value === 'object'
    ? Object.entries(value).reduce((acc, [key, val]) => {
        acc[key.toLowerCase()] = val;
        return acc;
    }, {})
    : {});

/**
 * Normalize one `template_vars` entry to the `contexts` entity shape.
 *
 * The backend serializes entities in two flavors:
 * - player: `{FirstName, LastName, Handle, ID, Level, Rank}`
 * - resource: `{ID, Title, Owner, ResourceType, LinkType, Link}`
 *
 * Current payloads wrap the resource one level down: the entry carries
 * `LinkType` + `Link` while the resource itself sits under
 * `TemplateVarResource`. Both shapes are flattened into the same field bag
 * before being read.
 *
 * `Link` carries the route material and varies per type: `{ID, Slug}` for a
 * challenge, `{ChannelID, ChannelSlug, PlatformID, PlatformSlug, PlatformType,
 * PlatformURL}` for a topic/forum context.
 *
 * @param {object} entity
 * @return {object|undefined}
 */
const normalizeTemplateVar = (entity) => {
    if (!entity || typeof entity !== 'object') return undefined;

    const outer = lowerKeys(entity);
    // Wrapped resource: the resource fields win, the link material stays outside.
    const fields = { ...outer, ...lowerKeys(outer.templatevarresource) };

    const type = fields.resourcetype || fields.linktype;
    const isPlayer = fields.handle != null || fields.firstname != null || type === 'user' || !type;

    if (isPlayer) {
        const name = [fields.firstname, fields.lastname].filter(Boolean).join(' ') || fields.handle;

        return {
            context_type: 'user',
            context_id: fields.id,
            id: fields.id,
            handle: fields.handle,
            screen_name: fields.handle,
            first_name: fields.firstname,
            last_name: fields.lastname,
            name,
            full_name: name,
            level: fields.level,
            rank: fields.rank,
        };
    }

    const link = lowerKeys(fields.link);

    return {
        context_type: type,
        context_id: fields.id,
        id: fields.id,
        title: fields.title,
        owner: fields.owner,
        slug: link.slug,
        // Forum-shaped links address a platform/channel pair instead of a slug.
        ...(link.channelslug && {
            channel: { id: link.channelid, slug: link.channelslug },
        }),
        ...(link.platformslug && {
            platform: {
                id: link.platformid,
                slug: link.platformslug,
                type: link.platformtype,
                url: link.platformurl,
            },
        }),
    };
};

/**
 * Map the response-level `template_vars` bag to `contexts`. Keys come through
 * as authored (`challenge`, `catalyst`, `idea`, `context`, `vote`, …) so admin
 * tokens like `{challenge.title}` / `{catalyst.name}` resolve directly.
 *
 * The generic `context` slot holds whatever the event happened in (a topic, a
 * challenge, …); resource entities are also aliased under their own type so
 * `{topic.title}` resolves, without shadowing an explicit entry. Players are
 * never aliased — they arrive keyed by role (catalyst, analyst, …) and `user`
 * is reserved for the viewer in buildContext.
 *
 * @param {object} vars
 * @return {object}
 */
const contextsFromTemplateVars = (vars = {}) => Object.entries(vars).reduce((acc, [key, value]) => {
    const entity = normalizeTemplateVar(value);

    if (!entity) return acc;

    acc[key] = entity;

    const alias = entity.context_type;

    if (alias && alias !== 'user' && !(alias in acc) && !(alias in vars)) {
        acc[alias] = entity;
    }

    return acc;
}, {});

/**
 * Hydrate a highlight's entities and its admin template.
 *
 * Entities come from `template_vars`, keyed as authored (`catalyst`,
 * `challenge`, `idea`, `mentor`, …) and exposed under `contexts`, so admin
 * tokens like `{catalyst.name}` / `{challenge.title}` resolve directly — the
 * same seam neurons use (hydrateNeuron). Payloads that predate it carry
 * `object_context` entries resolved against the response-level `relationships`
 * map (keyed `${context_type}:${context_id}`, each user entry keyed by its own
 * `subject`); those are mapped underneath and lose to `template_vars`.
 *
 * The admin template comes from the response-level catalog, keyed by
 * `meaningful_name` (e.g. `CatalystIdeaPicked.IdeaLikedCatalyst`) and falling
 * back to the older `${role}.${event_type}.${meaningful_name}` key. An inline
 * `template` on the item wins; realtime items arrive without one and resolve
 * against the catalog that came in the first request. Items left without a
 * template are filtered out by `useHighlights`.
 *
 * @param {object} item
 * @param {object} relationships
 * @param {object} templates
 * @return {object}
 */
export const hydrateHighlight = (item, relationships = {}, templates = {}) => ({
    ...item,
    template: item.template
        ?? templates[item.meaningful_name]
        ?? templates[`${item.role}.${item.event_type || item.event}.${item.meaningful_name}`],
    contexts: {
        ...(item.object_context || []).reduce((acc, ctx) => {
            acc[ctx.subject || ctx.context_type] = {
                ...ctx,
                ...relationships[`${ctx.context_type}:${ctx.context_id}`],
            };
            return acc;
        }, {}),
        ...contextsFromTemplateVars(item.template_vars),
    },
});


/**
 * Normalize a neuron to the same `contexts` shape highlights carry
 * (hydrateHighlight), so templates and route tokens resolve identically for
 * both notification kinds. Entities come from `template_vars`; the legacy flat
 * `challenge_*` fields are still mapped underneath for payloads that predate
 * it (cached pages, older realtime events) and lose to `template_vars`.
 *
 * Also attaches the admin template, keyed in the response-level catalog as
 * `${role}.${event_created}.${meaningful_name}` (e.g.
 * `innovator.IdeaRated.RatedMentorComment`), falling back to a catalog keyed by
 * `meaningful_name` alone (the shape highlights now ship); items without a
 * catalog entry keep `template` undefined and are filtered out by `useNeurons`.
 *
 * @param {object} item
 * @param {object} templates
 * @return {object}
 */
export const hydrateNeuron = (item, templates = {}) => ({
    template: templates[`${item.role}.${item.event_created}.${item.meaningful_name}`]
        ?? templates[item.meaningful_name],
    contexts: {
        ...((item.challenge_id || item.challenge_slug || item.challenge_title) && {
            challenge: {
                context_type: 'challenge',
                context_id: item.challenge_id,
                slug: item.challenge_slug,
                title: item.challenge_title,
                catalyst: item.challenge_owner,
            },
        }),
        ...(item.challenge_owner && {
            catalyst: {
                context_type: 'user',
                context_id: item.challenge_owner,
            },
        }),
        ...contextsFromTemplateVars(item.template_vars),
    },
    ...item,
});

export const useBrain = (handle, params) => {
    const enabled = computed(() => !!toValue(handle));

    return useQuery({
        queryKey: ['gamedash', 'brain', handle, params],
        queryFn: async () => {
            const { data: response } = await api.brain.neurons(
                toValue(handle), { ...toValue(params), size: 1 },
            );

            const brain = {
                ...response.relationships,
                total_notifications: response.meta.total,
            }

            return brain;
        },
        throwOnError: false,
        // refetchInterval: 1000 * 60, // 1 minute
        enabled,
    });
}

export const useNeurons = (handle, params = {}) => {
    const { auth } = useAuth();
    const router = useRouter();
    const enabled = computed(() => !!toValue(handle));

    const { data, ...passthrough } = useInfiniteQuery({
        queryKey: ['gamedash', 'neurons', handle, params],
        queryFn: async ({ pageParam }) => {
            const { data: response } = await api.brain.neurons(
                toValue(handle), { size: 20, page: pageParam, ...toValue(params) },
            );

            return response;
        },
        getNextPageParam: (lastResult) => {
            const { page, total } = lastResult.meta;
            const size = toValue(params).size ?? 20;

            if ((page + 1) * size >= total) {
                return undefined;
            }

            return page + 1;
        },
        throwOnError: false,
        initialPageParam: 0,
        enabled,
    });

    // Flatten paginated neurons and attach their admin templates. Neurons are
    // rendered from the admin template alone, so an item whose definition has
    // no template is dropped here — filtering at the source keeps the list
    // counts, empty states and virtual-list geometry honest. Adjacent items
    // rendering the same statement collapse into the newest one (the list runs
    // newest-first), which carries the run length as `repeats` and the run's
    // summed `points`; a different item in between breaks the run.
    const items = computed(() => (data.value?.pages || []).flatMap(
        (page) => (page.data || []).map((item) => hydrateNeuron(item, page.templates)),
    ).filter(hasTemplate).reduce((acc, item) => {
        const statement = renderNeuron(item, { router, user: auth.user });
        const last = acc.at(-1);

        if (!statement) return acc;

        if (last?.statement === statement) {
            last.repeats += 1;
            last.points = (Number(last.points) || 0) + (Number(item.points) || 0);
        } else {
            acc.push({ ...item, statement, repeats: 1 });
        }

        return acc;
    }, []));

    const meta = computed(() => data.value?.pages.at(-1)?.meta || {});
    const relationships = computed(() => data.value?.pages.at(-1)?.relationships || {});

    return {
        ...passthrough,
        data: items,
        meta,
        relationships,
    };
}

export const useHighlights = (params = {}) => {
    const { auth } = useAuth();
    const router = useRouter();
    const enabled = computed(() => auth.authenticated);

    const { data, ...passthrough } = useInfiniteQuery({
        queryKey: ['gamedash', 'highlights', params],
        queryFn: async ({ pageParam }) => {
            const { data: response } = await api.game.highlights({
                limit: 20, ...toValue(params), offset: pageParam,
            });

            return response;
        },
        getNextPageParam: (lastPage) => {
            const { total = 0, limit = 0, offset = 0 } = lastPage?.metadata  || lastPage?.meta || {};
            const next = offset + limit;

            return next < total ? next : undefined;
        },
        throwOnError: false,
        initialPageParam: 0,
        enabled,
    });

    // Flatten paginated highlights and hydrate their contexts + admin templates.
    // Highlights render from the admin template alone, so items without one are
    // dropped here — same seam as useNeurons, keeping counts, empty states and
    // virtual-list geometry honest. Adjacent items rendering the same statement
    // collapse into the newest one (the list runs newest-first), which carries
    // the run length as `repeats`; a different item in between breaks the run.
    const items = computed(() => (data.value?.pages || []).flatMap(
        (page) => (page.data || []).map((item) => hydrateHighlight(item, page.relationships, page.templates)),
    ).filter(hasTemplate).reduce((acc, item) => {
        const statement = renderHighlight(item, { router, user: auth.user });
        const last = acc.at(-1);

        if (!statement) return acc;

        if (last?.statement === statement) {
            last.repeats += 1;
        } else {
            acc.push({ ...item, statement, repeats: 1 });
        }

        return acc;
    }, []));

    const meta = computed(() => data.value?.pages.at(-1)?.metadata || {});

    return {
        ...passthrough,
        data: items,
        meta,
    };
}

